// The island quick-view's FIT rules (`native-apps/shared/outreach-needs-you/contract.json` rule 4, "FIT"), pure so
// they are unit-testable without a DOM. The panel measures; these decide.
//
// Reported on a phone: three long approval cards ran below the fold with no way to scroll. So:
//  1. the panel never extends past the screen — its height is capped to the space below the pill, and its body
//     scrolls inside that cap (`quickViewHeightCap`);
//  2. the tasks section shows 3 cards only when ALL 3 fit inside the cap; otherwise 2, so the reader sees whole
//     cards rather than one cut at the fold (`fittingTaskCards`). Whatever is left out is reached through the
//     section's existing "see all" overflow button to Tasks › Action needed.
// iOS and Android implement the same two rules over their own measurements.

/** The panel's height cap: the viewport below the pill's top edge, less the gap the panel keeps from the bottom
 *  edge. Never negative. `viewportHeight` is the visible viewport (`visualViewport.height`, else `innerHeight`). */
export function quickViewHeightCap(viewportHeight: number, pillTop: number, edgeGap: number): number {
  return Math.max(0, Math.floor(viewportHeight - Math.max(0, pillTop) - edgeGap));
}

/** The most task cards the section ever shows — `QUICK_VIEW_SHOWN` in `lib/island-quickview.ts`. */
export const FIT_MAX_CARDS = 3;
/** What the section falls back to when the third card would not fit whole. */
export const FIT_MIN_CARDS = 2;

/**
 * How many of the task cards to show.
 *
 * `cardHeights` are the rendered heights of the candidate cards in order (up to three; extra entries are
 * ignored). `space` is the height the cap leaves for the card list — the cap less everything above the list
 * (the header, the body's top inset, the section heading) — and `gap` the spacing between two cards (a
 * separator included).
 *
 * Three cards only when all three fit whole inside `space`; else two. Fewer than three candidates are all shown.
 * Before anything is measured (any height 0 or not finite) the answer is the cautious two.
 */
export function fittingTaskCards(cardHeights: readonly number[], space: number, gap: number): number {
  const candidates = cardHeights.slice(0, FIT_MAX_CARDS);
  if (candidates.length < FIT_MAX_CARDS) return candidates.length;
  if (candidates.some((height) => !Number.isFinite(height) || height <= 0)) return FIT_MIN_CARDS;
  const needed = candidates.reduce((sum, height) => sum + height, 0) + gap * (FIT_MAX_CARDS - 1);
  return needed <= space ? FIT_MAX_CARDS : FIT_MIN_CARDS;
}
